import { DOCUMENT } from '@angular/common';
import { Injectable, Inject } from '@angular/core';
import { LocalStorageService } from '@services/browser-storage/browser-storage.service';

type ThemeValue = 'dark' | 'light';

const THEME_STORAGE_KEY: string = 'ts-app-theme';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private current_theme: ThemeValue = 'light';

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private storage_svc: LocalStorageService
  ) {}

  initialize(): void {
    this.current_theme = this.preferredTheme();
    this.applyTheme(this.current_theme);
  }

  currentTheme(): ThemeValue {
    return this.current_theme;
  }

  isDarkTheme(): boolean {
    return this.current_theme === 'dark';
  }

  toggleTheme(): void {
    this.setTheme(this.current_theme === 'dark' ? 'light' : 'dark');
  }

  toggleLightTheme(): void {
    this.setTheme('light');
  }

  toggleDarkTheme(): void {
    this.setTheme('dark');
  }

  private setTheme(theme: ThemeValue): void {
    if (theme === this.current_theme) return;

    this.current_theme = theme;
    this.storage_svc.setItem<string>(THEME_STORAGE_KEY, theme);
    this.applyTheme(theme);
  }

  private preferredTheme(): ThemeValue {
    let stored_theme: string | undefined = this.storage_svc.getItem<string>(THEME_STORAGE_KEY);
    if (stored_theme === 'dark' || stored_theme === 'light') return stored_theme;

    return this.document.defaultView?.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  }

  private applyTheme(theme: ThemeValue): void {
    this.document.documentElement.dataset['bsTheme'] = theme;
  }
}
